/*
 * Copyright (c) 2023 Bolmate B.V.
 */

import React, {useContext, useEffect, useMemo, useState} from 'react';
import {Elements} from '@stripe/react-stripe-js';
import {loadStripe} from '@stripe/stripe-js';

import {
    AppNavigator, directTimeout, getJwt, getMode, getSetting, Loader, remToPx, ResponseData, showDialog, useEvent
} from '@dvrd/dvr-controls';
import {AppContext} from './components/misc/appContext';
import {startIdleObserver, stopIdleObserver} from './components/misc/idleObserver';
import PopupController from './components/misc/popupController';
import User from './models/userModel';
import {onAddAccount, onGlobalLoad} from './util/events';
import {initSession, logout} from './util/requestSender';
import {setSentryUser} from './util/sentryUtil';
import {setGaUserId} from './util/analytics';
import {getAdminJwt, onAdminLogout} from './util/adminUtil';
import HeaderController from './components/header/headerController';
import {setMenuWidth} from './util/menuUtil';
import {deleteFromLocal, getFromLocal, saveToAppLocal} from './util/storageUtil';
import Account from './models/accountModel';
import {clearBeaconIds, setBeaconIds} from './util/helpscoutUtil';
import {deleteStorageJWT, JWTCookieName, setJWT} from './util/jwtUtil';
import {googleLogout, GoogleOAuthProvider} from '@react-oauth/google';
import {Outlet, useNavigate} from 'react-router';
import {LOGIN_ROUTE, SELECTED_ACCOUNT_KEY} from './util/constants';
import SidebarMenuController from './components/sidebarMenu/sidebarMenuController';
import {requireMaintenanceLogin} from './util/maintenanceUtil';
import {getDefaultRoute} from './util/accessUtil';
import classNames from 'classnames';
import WarningMessage from './components/misc/messages/warningMessage';

function demoLogin(): boolean {
    const params = new URLSearchParams(location.search);
    if (params.get('demo') === '1') {
        setJWT(null);
        setGaUserId(null);
        return true;
    }
    return false;
}

function getSelectedAccount(user: User): Account | null {
    const {accounts} = user;
    if (accounts.length) {
        const selectedId = getFromLocal(SELECTED_ACCOUNT_KEY) ?? accounts[0].id;
        for (const account of accounts)
            if (account.id === selectedId) return account;
        return accounts[0];
    }
    return null;
}

function renderingCalculator(): boolean {
    return window.location.pathname.includes('prijs-calculator');
}

const devMode = getMode() === 'dev';

export default function App() {
    const context = useContext(AppContext);
    const {user} = context.userContext;
    const [validatingUser, setValidatingUser] = useState(true);
    const [menuWidth, _setMenuWidth] = useState(remToPx(12.5));
    const [mobileBannerVisible, setMobileBannerVisible] = useState(true);
    const stripe = useMemo(() => {
        const stripeApiKey: string = getSetting('stripeApiKey');
        return loadStripe(stripeApiKey);
    }, []);
    const navigate = useNavigate();
    useEvent('onLogout', _onLogout);

    function onHideMobileBanner() {
        setMobileBannerVisible(false);
    }

    function _onLogout() {
        if (!context.userContext.user) return;
        onGlobalLoad(true);
        logout({
            callback: (data: ResponseData) => {
                onGlobalLoad(false);
                if (data.success) {
                    clearBeaconIds();
                    googleLogout();
                    const logoutHandled = onAdminLogout(context);
                    if (!logoutHandled) {
                        deleteFromLocal(SELECTED_ACCOUNT_KEY);
                        setJWT(null);
                        setGaUserId(null);
                        context.onChangeContext({accounts: [], account: null, user: null});
                        navigate(LOGIN_ROUTE);
                    }
                } else showDialog('Het uitloggen is niet gelukt.', 'Uitloggen mislukt');
            }
        });
    }

    function validateUser() {
        if (requireMaintenanceLogin()) {
            setValidatingUser(false);
            return;
        }
        if (!getJwt(JWTCookieName) || (demoLogin() && !devMode))
            return startApplication(null);
        User.get(null, (user: User | null) => {
            if (!user || (user.isDemoAccount && !devMode)) {
                setJWT(null);
                return startApplication(null);
            }
            setGaUserId(user.id);
            const account = getSelectedAccount(user);
            setBeaconIds(user, account);
            if (user.isNewUser) saveToAppLocal('newUser', true);
            context.onChangeContext({
                user,
                accounts: user.accounts,
                account: account
            }, () => {
                if (account) setSentryUser(user, account);
                else {
                    setSentryUser(user);
                    directTimeout(onAddAccount);
                }
                startApplication(user, account);
            });
        }, () => {
            setJWT(null);
            startApplication(null);
        });
    }

    function startApplication(user: User | null, account?: Account | null) {
        setValidatingUser(false);
        if (user && (!user.isDemoAccount || devMode)) {
            if (/inloggen|registreren/.test(window.location.pathname)) {
                const defaultRoute = getDefaultRoute(account?.userRights, account?.userRole);
                navigate(defaultRoute);
            }
        } else {
            let currentPath = encodeURIComponent(location.pathname);
            if (!/inloggen|registreren|(wachtwoord.*)|2fa-reset/.test(currentPath))
                navigate(`/inloggen?redirect=${currentPath}`);
        }
    }

    function onSetMenuWidth() {
        const element = document.getElementById('bolmate-menu');
        if (!element) return;
        const width = element.getBoundingClientRect().width;
        _setMenuWidth(width);
    }

    function replaceBeaconIcon() {
        // TODO Currently not enabled
        return;
        // const iFrame: HTMLIFrameElement | null = document.querySelector('iframe[data-cy=FrameComponent]');
        // if (!iFrame) return;
        // const iconContainer: HTMLSpanElement | null | undefined = iFrame.contentWindow?.document.querySelector(
        //     'span.c-FabButton__icon--icon');
        // if (!iconContainer) return console.debug('iconContainer not found');
        // const imageSource = getImg('dave-levi')[0];
        // const style = '"border-radius: 200px;"';
        // iconContainer.innerHTML = `<img src="${imageSource[0]}" alt="" class="beacon-image" style=${style}/>`;
    }

    useEffect(() => {
        setMenuWidth(menuWidth);
    }, [menuWidth]);

    useEffect(() => {
        if (user && !menuWidth) window.setTimeout(onSetMenuWidth, 500);
    }, [user]);

    useEffect(() => {
        window.Beacon('on', 'ready', replaceBeaconIcon);
        initSession(() => {
            deleteStorageJWT();
            getAdminJwt();
            validateUser();
            startIdleObserver();
        });
        return function() {
            stopIdleObserver();
        };
    }, []);

    if (validatingUser) return <Loader active/>;
    return (
        <GoogleOAuthProvider clientId='85081984902-vov3r4rckqdh843l7pf8r8bdsbqqhe7u'>
            <AppNavigator/>
            {(!!user && !renderingCalculator()) ? <>
                    <HeaderController menuWidth={menuWidth}/>
                    <div className='page-content'>
                        <SidebarMenuController/>
                        {/*@ts-ignore*/}
                        <Elements stripe={stripe}>
                            <div className='page'>
                                <Outlet/>
                            </div>
                        </Elements>
                    </div>
                </> :
                <div className='page'>
                    <Outlet/>
                </div>}
            <PopupController/>
            <WarningMessage className={classNames('mobile-banner', mobileBannerVisible && 'visible')}
                            onClose={onHideMobileBanner} closeText='x'>
                Ons platform is niet geoptimaliseerd voor mobiele apparaten. Switch naar een laptop of desktop.
            </WarningMessage>
        </GoogleOAuthProvider>
    );
}